import React, { useCallback, useState } from "react";
import AiFeatureToolPrep from "../../shared/Popup/AiFeatureToolPrep";
import AiMaskPaintPreview from "../../shared/Popup/AiMaskPaintPreview";
import AiMaskSelectToolControls from "../../shared/Popup/AiMaskSelectToolControls";
import { useAiMaskPaint } from "../../shared/Popup/useAiMaskPaint";
import { replaceObjectSingle } from "../../../../../services/api/tools/replace_object";
import { resolveMaskEditSource } from "../../shared/Popup/maskFollowUpSource";

/**
 * AI Tools → AI Replace Object prep (Smart / Brush / Box / Eraser + replace prompt).
 */
const ReplaceObjectToolPrep = (props) => {
  const [whatToReplace, setWhatToReplace] = useState("");
  const [prompt, setPrompt] = useState("");
  const paint = useAiMaskPaint();
  const toolId = paint.paintTool;
  const maskMode = toolId !== "smart";

  const isGenerateReady = useCallback(() => {
    if (!prompt.trim()) return false;
    if (toolId === "smart") return Boolean(whatToReplace.trim());
    return paint.hasMask;
  }, [toolId, paint.hasMask, prompt, whatToReplace]);

  const handleGenerate = useCallback(
    async ({ token, file, image_url, resultImageUrl, stageKey }) => {
      const replacement = prompt.trim();
      const described = whatToReplace.trim() || undefined;
      if (!replacement) {
        throw new Error("Describe what to replace it with.");
      }
      let mask = null;
      if (toolId !== "smart" && paint.hasMask) {
        const blob = await paint.maskRef.current?.exportMaskBlob?.();
        if (blob) {
          mask = new File([blob], "mask.png", { type: "image/png" });
        }
      }
      const target = mask ? undefined : described;
      if (!target && !mask) {
        throw new Error("Describe what to replace or paint a mask.");
      }

      const input = await resolveMaskEditSource({
        token,
        file,
        image_url,
        resultImageUrl,
        stageKey,
        hasMask: Boolean(mask),
        filename: "replace-object-input.png",
      });

      return replaceObjectSingle(token, input.file, {
        replacement,
        target,
        mask,
        output_format: "webp",
        ...(input.image_url ? { image_url: input.image_url } : {}),
      });
    },
    [paint.hasMask, paint.maskRef, prompt, toolId, whatToReplace]
  );

  return (
    <AiFeatureToolPrep
      {...props}
      onGenerate={handleGenerate}
      generateFileName="replace-object-input.png"
      toolKey="ai_replace_object"
      title="AI Replace Object"
      aka="Object Replacer"
      bio="Swap an object for something new while matching the scene."
      emptyPreviewLabel="Upload an image to preview object replace"
      loadingLabel="Replacing object…"
      emptyFooterHint="Pick a tool, describe the replacement, then generate."
      isGenerateReady={isGenerateReady}
      renderPreview={({
        imageUrl,
        loading,
        loadingLabel,
        emptyLabel,
        hasResult,
      }) => (
        <AiMaskPaintPreview
          ref={paint.maskRef}
          imageUrl={imageUrl}
          loading={loading}
          loadingLabel={loadingLabel}
          emptyLabel={emptyLabel}
          tool={toolId}
          brushSize={paint.brushSize}
          paintEnabled={
            Boolean(imageUrl) && !loading && toolId !== "smart"
          }
          clearVersion={paint.clearVersion}
          onMaskChange={paint.handleMaskChange}
        />
      )}
      renderControls={({ busy, leftPanelRef, sourceImageUrl }) => (
        <>
          <AiMaskSelectToolControls
            paint={paint}
            busy={busy}
            sourceImageUrl={sourceImageUrl}
            leftPanelRef={leftPanelRef}
            subject="object"
          />
          <div className="aftp-section">
            <label className="aftp-label" htmlFor="aftp-rpo-target">
              What to replace
              {maskMode ? (
                <span className="aftp-optional"> (optional)</span>
              ) : null}
            </label>
            <textarea
              id="aftp-rpo-target"
              className="aftp-input"
              rows={3}
              value={whatToReplace}
              onChange={(e) => {
                setWhatToReplace(e.target.value);
              }}
              placeholder="e.g. the plastic chair, the person on the left"
              disabled={busy || maskMode}
            />
          </div>
          <div className="aftp-section">
            <label className="aftp-label" htmlFor="aftp-rpo-prompt">
              Replace with
            </label>
            <textarea
              id="aftp-rpo-prompt"
              className="aftp-input"
              rows={3}
              value={prompt}
              onChange={(e) => {
                setPrompt(e.target.value);
              }}
              placeholder="e.g. a red ceramic vase, a wooden chair"
              disabled={busy}
            />
          </div>
        </>
      )}
    />
  );
};

export default ReplaceObjectToolPrep;
